import React, { useEffect, useState } from "react";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function api(path, opts = {}) {
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: "include",
  }).then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      let msg;
      try {
        msg = JSON.parse(text).error || text;
      } catch {
        msg = text;
      }
      throw new Error(msg || `${res.status} ${res.statusText}`);
    }
    return res.status === 204 ? null : res.json();
  });
}

const SCHEDULE_OPTIONS = ["Daily", "Weekly", "Monthly", "Quarterly", "Semi-Annually", "Yearly"];

function readCurrentUserEmail() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return (u && u.email) || "";
  } catch {
    return "";
  }
}

const emptyDraft = () => ({
  id: null,
  email: readCurrentUserEmail(),
  schedule: "Daily",
  prompt: "",
  status: "active",
});

export default function ScheduledReportsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState(emptyDraft());
  const [formOpen, setFormOpen] = useState(false);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await api("/scheduled-reports");
      setRows(data || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openNew() {
    setDraft(emptyDraft());
    setFormOpen(true);
  }

  function openEdit(r) {
    setDraft({ ...r });
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setDraft(emptyDraft());
  }

  async function save() {
    setErr("");
    const body = {
      prompt: draft.prompt,
      email: draft.email,
      schedule: draft.schedule,
      status: draft.status,
    };
    try {
      if (draft.id) {
        await api(`/scheduled-reports/${draft.id}`, { method: "PUT", body });
      } else {
        await api("/scheduled-reports", { method: "POST", body });
      }
      closeForm();
      await load();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function toggleStatus(r) {
    setErr("");
    const next = r.status === "active" ? "inactive" : "active";
    try {
      await api(`/scheduled-reports/${r.id}`, { method: "PUT", body: { status: next } });
      await load();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function remove(r) {
    if (!window.confirm("Delete this scheduled report?")) return;
    setErr("");
    try {
      await api(`/scheduled-reports/${r.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setErr(e.message);
    }
  }

  const promptCount = (draft.prompt || "").length;
  const promptOver = promptCount > 255;
  const saveDisabled =
    !draft.prompt.trim() ||
    !draft.email.trim() ||
    !SCHEDULE_OPTIONS.includes(draft.schedule) ||
    promptOver;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ margin: 0, flex: 1 }}>Scheduled Reports</h2>
        <button type="button" onClick={openNew} className="btn btn-primary" style={btnPrimary}>
          + New Report
        </button>
      </div>
      <div style={{ color: "#555", marginBottom: 16, fontSize: 14 }}>
        Reports run on the chosen schedule at 6:00 AM and are delivered to the email address you specify.
      </div>

      {err && <div style={errBox}>{err}</div>}

      <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead style={{ background: "#f5f5f5" }}>
            <tr>
              <th style={th}>Prompt</th>
              <th style={th}>Email</th>
              <th style={th}>Schedule</th>
              <th style={th}>Status</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={5} style={{ padding: 16, textAlign: "center", color: "#888" }}>
                  No scheduled reports yet. Click "+ New Report" to create one.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid #eee" }}>
                <td style={{ ...td, maxWidth: 420 }} title={r.prompt || ""}>
                  {r.prompt}
                </td>
                <td style={td}>{r.email}</td>
                <td style={td}>{r.schedule}</td>
                <td style={td}>
                  <span style={r.status === "active" ? activeChip : inactiveChip}>{r.status}</span>
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  <button type="button" onClick={() => openEdit(r)} className="btn btn-secondary" style={btnSmall}>
                    Edit
                  </button>
                  <button type="button" onClick={() => toggleStatus(r)} className="btn btn-secondary" style={btnSmall}>
                    {r.status === "active" ? "Disable" : "Enable"}
                  </button>
                  <button type="button" onClick={() => remove(r)} className="btn btn-danger" style={{ ...btnSmall, color: "#a31919" }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <div style={modalBackdrop} onClick={closeForm}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{draft.id ? "Edit Scheduled Report" : "New Scheduled Report"}</h3>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Prompt
                <textarea
                  value={draft.prompt}
                  onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
                  maxLength={255}
                  rows={4}
                  placeholder="The question Aegis should answer on each run"
                  style={{ width: "100%", padding: 8, fontFamily: "inherit", boxSizing: "border-box" }}
                />
                <div style={{ fontSize: 11, color: promptOver ? "#a31919" : "#777", textAlign: "right" }}>
                  {promptCount} / 255
                </div>
              </label>

              <label style={{ fontSize: 13 }}>
                Delivery email
                <input
                  type="email"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  maxLength={255}
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                />
              </label>

              <label style={{ fontSize: 13 }}>
                Schedule
                <select
                  value={draft.schedule}
                  onChange={(e) => setDraft({ ...draft, schedule: e.target.value })}
                  style={{ width: "100%", padding: 8 }}
                >
                  {SCHEDULE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
                  Reports run at 6:00 AM on the chosen cadence.
                </div>
              </label>

              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={draft.status === "active"}
                  onChange={(e) => setDraft({ ...draft, status: e.target.checked ? "active" : "inactive" })}
                />
                Active
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button type="button" onClick={closeForm} className="btn btn-secondary" style={btnPrimary}>
                Cancel
              </button>
              <button type="button" onClick={save} disabled={saveDisabled} className="btn btn-primary" style={{ ...btnPrimary, opacity: saveDisabled ? 0.5 : 1 }}>
                {draft.id ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 12, textTransform: "uppercase", color: "#444" };
const td = { padding: "8px 10px", verticalAlign: "top" };
const btnPrimary = { padding: "6px 12px" };
const btnSmall = { padding: "4px 8px", marginRight: 6, fontSize: 12 };
const errBox = { background: "#fde2e1", color: "#a31919", padding: 8, borderRadius: 4, marginBottom: 12 };
const activeChip = { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, background: "#d1f7dc", color: "#13651f" };
const inactiveChip = { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, background: "#eee", color: "#666" };
const modalBackdrop = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalCard = { background: "#fff", padding: 20, borderRadius: 6, width: "min(560px, 92vw)", maxHeight: "90vh", overflow: "auto" };
